import { getCurrency, isKnownCurrencyCode } from './currency';
import type { Money } from './money';
import { Money as M } from './money';

export interface FormatMoneyOptions {
  /**
   * BCP 47 locale tag. Defaults to `'en-US'` when not provided or when the
   * runtime fails to construct `Intl.NumberFormat` for the given tag.
   */
  locale?: string;
  /**
   * Style of currency symbol in the output. Maps to `Intl.NumberFormat`'s
   * `currencyDisplay`. Defaults to `'symbol'`.
   */
  currencyDisplay?: 'symbol' | 'narrowSymbol' | 'code' | 'name';
  /**
   * When `true`, render the sign with accounting parentheses for
   * negatives (e.g. `($1,234.56)`). Defaults to `false`.
   */
  accounting?: boolean;
}

const DEFAULT_LOCALE = 'en-US';

/**
 * Locale-aware formatting for a {@link Money}. Uses `Intl.NumberFormat`
 * when available (all modern JS runtimes, including Hermes with ICU).
 * Falls back to a deterministic "symbol + fixed-decimal" renderer if
 * `Intl` is unavailable or throws.
 */
export function formatMoney(value: Money, options: FormatMoneyOptions = {}): string {
  const locale = options.locale ?? DEFAULT_LOCALE;
  const currencyDisplay = options.currencyDisplay ?? 'symbol';
  const accounting = options.accounting ?? false;

  const major = M.toDecimalString(value);

  if (isKnownCurrencyCode(value.currency)) {
    try {
      const nf = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: value.currency,
        currencyDisplay,
        minimumFractionDigits: value.scale,
        maximumFractionDigits: value.scale,
        currencySign: accounting ? 'accounting' : 'standard',
      });
      // `Intl.NumberFormat` accepts numeric strings, which preserves
      // precision past 2^53 for large totals.
      return nf.format(major as unknown as number);
    } catch {
      return fallbackFormat(value, major);
    }
  }
  return fallbackFormat(value, major);
}

function fallbackFormat(value: Money, decimal: string): string {
  const info = getCurrency(value.currency);
  const negative = decimal.startsWith('-');
  const body = negative ? decimal.slice(1) : decimal;
  const withGroups = addThousandSeparators(body, ',');
  const separator = isKnownCurrencyCode(value.currency) ? '' : ' ';
  const withSymbol =
    info.symbolPosition === 'before'
      ? `${info.symbol}${separator}${withGroups}`
      : `${withGroups}${separator}${info.symbol}`;
  return negative ? `-${withSymbol}` : withSymbol;
}

function addThousandSeparators(decimal: string, sep: string): string {
  const dot = decimal.indexOf('.');
  const whole = dot >= 0 ? decimal.slice(0, dot) : decimal;
  const frac = dot >= 0 ? decimal.slice(dot) : '';
  const withSeps = whole.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  return `${withSeps}${frac}`;
}
